import EditIcon from '@mui/icons-material/Edit'
import LinkIcon from '@mui/icons-material/Link'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useToast } from '@/shared/toast/ToastProvider'

import { LaunchAudienceFilterTags } from './LaunchAudienceFilterTags'
import {
  buildLaunchAudienceTags,
  formatLaunchPublishedDate,
  formatLaunchReward,
  launchSurveyTypeLabel,
  ALL_REGISTERED_SURVEY_LABEL,
  DYNAMIC_SURVEY_LABEL,
  REGULAR_SURVEY_LABEL,
  LAUNCH_INSTRUMENT_GRID_COLUMNS,
  type LaunchSurveyTypeLabel,
} from '../lib/launchSurveyMetadata'

/** Survey-type pill colours — Dynamic (purple), All Registered (green, BFP-1141), Regular (blue). */
const SURVEY_TYPE_PILL_SX: Record<LaunchSurveyTypeLabel, { bgcolor: string; color: string }> = {
  [DYNAMIC_SURVEY_LABEL]: { bgcolor: '#e3dffc', color: '#3f2990' },
  [ALL_REGISTERED_SURVEY_LABEL]: { bgcolor: '#dcfce7', color: '#15803d' },
  [REGULAR_SURVEY_LABEL]: { bgcolor: '#e6f1fb', color: '#008cd0' },
}

import launchContinueDraftIcon from '@/assets/campaigns/launch/icon-continue-draft.svg'
import launchDeleteDraftIcon from '@/assets/campaigns/launch/icon-delete-draft.svg'
import launchDashboardGridIcon from '@/assets/campaigns/launch/icon-dashboard-grid.svg'
import launchPausePillIcon from '@/assets/campaigns/launch/icon-pause-pill.svg'
import launchPauseIcon from '@/assets/campaigns/launch/icon-pause.svg'
import launchPlayLargeIcon from '@/assets/campaigns/launch/icon-play-large.svg'
import launchStopIcon from '@/assets/campaigns/launch/icon-stop.svg'
import launchRemindIcon from '@/assets/campaigns/launch/icon-remind.svg'

import { resolveInstrumentLaunchStatusPill } from '../lib/instrument-launch-status'
import { launchStatusLabelWithTimestamp } from '../lib/formatInstrumentLifecycleTimestamp'
import type { CampaignInstrument } from '../types'

interface Props {
  instrument: CampaignInstrument
  /** 1-based LOI index — drives row background (first LOI = white, rest = Catskill 50%). */
  loiOrdinal: number
  /** Share the parent table's column tracks so headers and cells line up. */
  subgrid?: boolean
  canWrite: boolean
  onNavigateDashboard: (instrumentId: string) => void
  onContinueDraft: (instrumentId: string) => void
  onDeleteDraft: (instrument: CampaignInstrument) => void
  onReactivateClick: (instrument: CampaignInstrument) => void
  onPauseClick: (instrument: CampaignInstrument) => void
  onStopClick: (instrument: CampaignInstrument) => void
  /** BFP-1142 — edit a Live / Paused survey (pencil); hidden when omitted. */
  onEditLiveClick?: (instrument: CampaignInstrument) => void
  /** BFP-1158 — Live surveys only; hidden when omitted. */
  onRemindClick?: (instrument: CampaignInstrument) => void
}

/** Gap between icon buttons, and between icons and status label (same value). */
const ACTIONS_BTN_GAP = '14px'

const ACTIONS_ROW_SX = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: ACTIONS_BTN_GAP,
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
} as const

const ACTIONS_ROW_DASHBOARD_ONLY_SX = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: ACTIONS_BTN_GAP,
} as const

/** US-SM-3-1-31 — Launching Screen instrument row icon tooltips. */
const ICON_ACTION_TOOLTIPS = {
  dashboard: 'Check your instrument Analytical Information',
  draft: 'Edit your draft instrument',
  deleteDraft: 'Remove this draft survey',
  reactivate: 'Reactivate your paused instrument',
  pause: 'Pause your live instrument',
  stop: 'Stop your live instrument permanently',
  editLive: 'Edit and republish your live instrument',
  remind: 'Send reminder',
} as const

type IconActionVariant = keyof typeof ICON_ACTION_TOOLTIPS

const ICON_ACTION_STYLES: Record<
  IconActionVariant,
  { bg: string; src?: string; buttonSize: number; borderRadius: string }
> = {
  dashboard: { bg: '#C7EBF9', src: launchDashboardGridIcon, buttonSize: 28, borderRadius: '5px' },
  draft: { bg: '#C0D6F9', src: launchContinueDraftIcon, buttonSize: 28, borderRadius: '5px' },
  deleteDraft: { bg: '#FFF0F0', src: launchDeleteDraftIcon, buttonSize: 28, borderRadius: '5px' },
  reactivate: { bg: 'rgba(245, 158, 11, 0.2)', src: launchPlayLargeIcon, buttonSize: 28, borderRadius: '5px' },
  pause: { bg: 'rgba(245, 158, 11, 0.2)', src: launchPauseIcon, buttonSize: 28, borderRadius: '5px' },
  stop: { bg: '#FFF0F0', src: launchStopIcon, buttonSize: 28, borderRadius: '5px' },
  /** BFP-1142 Figma 219:2167 — Material edit glyph on light-blue tile. */
  editLive: { bg: '#C7EBF9', buttonSize: 28, borderRadius: '5px' },
  remind: { bg: '#E8F6EE', src: launchRemindIcon, buttonSize: 28, borderRadius: '5px' },
}

function iconActionDimensions(variant: IconActionVariant): { width: number; height: number } {
  if (variant === 'stop') return { width: 15, height: 15 }
  if (variant === 'pause') return { width: 14, height: 14 }
  if (variant === 'dashboard') return { width: 14, height: 14 }
  if (variant === 'reactivate') return { width: 15, height: 15 }
  if (variant === 'draft') return { width: 11, height: 10 }
  if (variant === 'deleteDraft') return { width: 16, height: 16 }
  return { width: 13, height: 13 }
}

function LaunchInstrumentIconButton(
  props: Readonly<{
    variant: IconActionVariant
    ariaLabel: string
    testId: string
    onClick: () => void
  }>,
) {
  const { variant, ariaLabel, testId, onClick } = props
  const style = ICON_ACTION_STYLES[variant]
  const iconSize = iconActionDimensions(variant)
  const tooltipTitle = ICON_ACTION_TOOLTIPS[variant]

  return (
    <Tooltip
      title={tooltipTitle}
      slotProps={{
        tooltip: {
          sx: { textAlign: 'center' },
        },
      }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={onClick}
        sx={{
          width: style.buttonSize,
          height: style.buttonSize,
          p: 0,
          borderRadius: style.borderRadius,
          bgcolor: style.bg,
          flexShrink: 0,
          '&:hover': { bgcolor: style.bg, opacity: 0.92 },
        }}
      >
        {style.src ? (
          <Box
            component="img"
            src={style.src}
            alt=""
            aria-hidden
            sx={{
              width: iconSize.width,
              height: iconSize.height,
              display: 'block',
              objectFit: 'contain',
            }}
          />
        ) : (
          <EditIcon aria-hidden sx={{ fontSize: 16, color: '#008CD0' }} />
        )}
      </IconButton>
    </Tooltip>
  )
}

function lifecyclePillLabel(
  instrument: CampaignInstrument,
  pill: { label: string },
): string {
  if (instrument.status === 'paused') {
    return launchStatusLabelWithTimestamp('Paused', instrument.pausedAt)
  }
  if (instrument.status === 'stopped') {
    return launchStatusLabelWithTimestamp('Stopped', instrument.stoppedAt)
  }
  return pill.label
}

function LaunchInstrumentStatusPill(
  props: Readonly<{
    label: string
    bg: string
    color: string
    iconSrc?: string
    iconSize?: number
  }>,
) {
  const { label, bg, color, iconSrc, iconSize = 9 } = props
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: iconSrc ? '4px' : 0,
        flexShrink: 0,
        px: '10px',
        py: '4px',
        borderRadius: 9999,
        bgcolor: bg,
      }}
    >
      {iconSrc ? (
        <Box
          component="img"
          src={iconSrc}
          alt=""
          aria-hidden
          sx={{ width: iconSize, height: iconSize, display: 'block', objectFit: 'contain' }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          color,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

function EditLiveAction({
  instrument,
  onEditLiveClick,
}: Readonly<{
  instrument: CampaignInstrument
  onEditLiveClick?: (instrument: CampaignInstrument) => void
}>) {
  if (!onEditLiveClick) return null
  return (
    <LaunchInstrumentIconButton
      variant="editLive"
      ariaLabel="Edit live instrument"
      testId={`launch-instrument-edit-live-${instrument.id}`}
      onClick={() => onEditLiveClick(instrument)}
    />
  )
}

type LaunchInstrumentActionProps = Readonly<{
  instrument: CampaignInstrument
  canWrite: boolean
  onNavigateDashboard: (instrumentId: string) => void
  onContinueDraft: (instrumentId: string) => void
  onDeleteDraft: (instrument: CampaignInstrument) => void
  onReactivateClick: (instrument: CampaignInstrument) => void
  onPauseClick: (instrument: CampaignInstrument) => void
  onStopClick: (instrument: CampaignInstrument) => void
  onEditLiveClick?: (instrument: CampaignInstrument) => void
  onRemindClick?: (instrument: CampaignInstrument) => void
}>

function LaunchInstrumentDashboardButton({
  instrumentId,
  onNavigateDashboard,
}: Readonly<{
  instrumentId: string
  onNavigateDashboard: (instrumentId: string) => void
}>) {
  return (
    <LaunchInstrumentIconButton
      variant="dashboard"
      ariaLabel="View instrument dashboard"
      testId={`launch-instrument-dashboard-${instrumentId}`}
      onClick={() => onNavigateDashboard(instrumentId)}
    />
  )
}

/** Status-specific icon clusters (Figma 6103:10959 and related). */
function LaunchInstrumentStatusActions(props: LaunchInstrumentActionProps) {
  const {
    instrument,
    canWrite,
    onNavigateDashboard,
    onContinueDraft,
    onDeleteDraft,
    onReactivateClick,
    onPauseClick,
    onStopClick,
    onEditLiveClick,
    onRemindClick,
  } = props
  const { id, status } = instrument

  if (status === 'draft') {
    return (
      <Box sx={ACTIONS_ROW_SX}>
        {canWrite ? (
          <>
            <LaunchInstrumentIconButton
              variant="draft"
              ariaLabel="Continue editing draft instrument"
              testId={`launch-instrument-continue-${id}`}
              onClick={() => onContinueDraft(id)}
            />
            <LaunchInstrumentIconButton
              variant="deleteDraft"
              ariaLabel="Remove this draft survey"
              testId={`launch-instrument-delete-${id}`}
              onClick={() => onDeleteDraft(instrument)}
            />
          </>
        ) : null}
      </Box>
    )
  }

  if (status === 'paused') {
    return (
      <Box sx={ACTIONS_ROW_SX}>
        <LaunchInstrumentDashboardButton instrumentId={id} onNavigateDashboard={onNavigateDashboard} />
        {canWrite ? (
          <>
            <LaunchInstrumentIconButton
              variant="reactivate"
              ariaLabel="Reactivate paused instrument"
              testId={`launch-instrument-reactivate-${id}`}
              onClick={() => onReactivateClick(instrument)}
            />
            <LaunchInstrumentIconButton
              variant="stop"
              ariaLabel="Stop instrument"
              testId={`launch-instrument-stop-${id}`}
              onClick={() => onStopClick(instrument)}
            />
            <EditLiveAction instrument={instrument} onEditLiveClick={onEditLiveClick} />
          </>
        ) : null}
      </Box>
    )
  }

  if (status === 'active') {
    return (
      <Box sx={ACTIONS_ROW_SX}>
        <LaunchInstrumentDashboardButton instrumentId={id} onNavigateDashboard={onNavigateDashboard} />
        {canWrite ? (
          <>
            <LaunchInstrumentIconButton
              variant="pause"
              ariaLabel="Pause instrument"
              testId={`launch-instrument-pause-${id}`}
              onClick={() => onPauseClick(instrument)}
            />
            <LaunchInstrumentIconButton
              variant="stop"
              ariaLabel="Stop instrument"
              testId={`launch-instrument-stop-${id}`}
              onClick={() => onStopClick(instrument)}
            />
            <EditLiveAction instrument={instrument} onEditLiveClick={onEditLiveClick} />
            {onRemindClick ? (
              <LaunchInstrumentIconButton
                variant="remind"
                ariaLabel="Send reminder"
                testId={`launch-instrument-remind-${id}`}
                onClick={() => onRemindClick(instrument)}
              />
            ) : null}
          </>
        ) : null}
      </Box>
    )
  }

  if (status === 'completed' || status === 'expired' || status === 'closed' || status === 'stopped') {
    return (
      <Box sx={ACTIONS_ROW_DASHBOARD_ONLY_SX}>
        <LaunchInstrumentDashboardButton instrumentId={id} onNavigateDashboard={onNavigateDashboard} />
      </Box>
    )
  }

  return null
}

/**
 * Figma action clusters (left → right after title):
 * - active: Live + dashboard + pause + stop
 * - draft: Draft label + continue (>>) when canWrite
 * - paused: Paused label + dashboard + play + stop (Figma 6103:10959)
 */
function LaunchInstrumentActions(props: LaunchInstrumentActionProps) {
  const { instrument } = props
  const { id, status } = instrument
  const notify = useToast()
  const shareUrl = instrument.publicSurveyUrl?.trim() ?? ''
  const showCopyLink = status !== 'draft' && shareUrl.length > 0

  const copySurveyLink = () => {
    void navigator.clipboard.writeText(shareUrl).then(() => {
      notify.showToast('Survey link copied to clipboard.', 'success')
    })
  }

  return (
    <Box
      data-testid={`launch-instrument-actions-${id}`}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: ACTIONS_BTN_GAP,
        flexShrink: 0,
      }}
    >
      {showCopyLink ? (
        <Tooltip title="Copy survey link">
          <IconButton
            size="small"
            aria-label="Copy survey link"
            data-testid={`launch-instrument-copy-link-${id}`}
            onClick={copySurveyLink}
            sx={{
              width: 28,
              height: 28,
              p: 0,
              borderRadius: '8px',
              bgcolor: '#c7ebf9',
              color: '#008cd0',
              flexShrink: 0,
              '&:hover': { bgcolor: '#c7ebf9', opacity: 0.92 },
            }}
          >
            <LinkIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      <LaunchInstrumentStatusActions {...props} />
    </Box>
  )
}

const CELL_SX = {
  minWidth: 0,
  px: '13px',
  pt: '21px',
  pb: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '10px',
  bgcolor: '#FFFFFF',
  borderBottom: '1px solid #E5E7EB',
} as const

const META_TEXT_SX = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#0F1729',
} as const

export function LaunchInstrumentRow({
  instrument,
  subgrid = false,
  canWrite,
  onNavigateDashboard,
  onContinueDraft,
  onDeleteDraft,
  onReactivateClick,
  onPauseClick,
  onStopClick,
  onEditLiveClick,
  onRemindClick,
}: Readonly<Props>) {
  const pill = resolveInstrumentLaunchStatusPill(instrument.status, instrument.closeReason)
  const surveyType = launchSurveyTypeLabel(instrument.audienceAvailability)
  const dynamicSurvey = surveyType === DYNAMIC_SURVEY_LABEL
  const allRegisteredSurvey = surveyType === ALL_REGISTERED_SURVEY_LABEL
  const audienceTags = buildLaunchAudienceTags(
    instrument.audienceFilters,
    dynamicSurvey,
    allRegisteredSurvey,
    instrument.profileAttributeFilters,
  )
  const surveyTypePill = SURVEY_TYPE_PILL_SX[surveyType]
  const responseCount = Math.max(0, instrument.responseCount ?? 0)

  return (
    <Box
      role="row"
      data-testid={`launch-instrument-row-${instrument.id}`}
      sx={
        subgrid
          ? { display: 'contents' }
          : {
              display: 'grid',
              gridTemplateColumns: LAUNCH_INSTRUMENT_GRID_COLUMNS,
              alignItems: 'stretch',
              bgcolor: '#FFFFFF',
              minHeight: 118,
            }
      }
    >
      <Box role="cell" sx={CELL_SX}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#0F1729',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {instrument.name}
        </Typography>
        <Box
          component="span"
          data-testid={`launch-instrument-survey-type-${instrument.id}`}
          sx={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            height: 26,
            px: '14px',
            borderRadius: '9999px',
            bgcolor: surveyTypePill.bgcolor,
            color: surveyTypePill.color,
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          {surveyType}
        </Box>
      </Box>
      <Box role="cell" sx={CELL_SX}>
        <LaunchAudienceFilterTags
          tags={audienceTags}
          testId={`launch-instrument-audience-${instrument.id}`}
        />
      </Box>
      <Box role="cell" sx={{ ...CELL_SX, gap: 0 }}>
        <Typography
          data-testid={`launch-instrument-response-count-${instrument.id}`}
          sx={META_TEXT_SX}
        >
          {responseCount.toLocaleString()}
        </Typography>
        <Typography
          data-testid={`launch-instrument-reward-${instrument.id}`}
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
            color: 'rgba(15, 23, 41, 0.6)',
          }}
        >
          {formatLaunchReward(instrument.rewardPerResponse)}
        </Typography>
      </Box>
      <Box role="cell" sx={CELL_SX}>
        <Typography
          data-testid={`launch-instrument-published-${instrument.id}`}
          sx={META_TEXT_SX}
        >
          {formatLaunchPublishedDate(instrument.publishedAt)}
        </Typography>
      </Box>
      <Box role="cell" sx={CELL_SX}>
        <LaunchInstrumentStatusPill
          {...pill}
          label={lifecyclePillLabel(instrument, pill)}
          iconSrc={instrument.status === 'paused' ? launchPausePillIcon : undefined}
          iconSize={9}
        />
      </Box>
      <Box role="cell" sx={{ ...CELL_SX, minWidth: 'max-content' }}>
        <LaunchInstrumentActions
          instrument={instrument}
          canWrite={canWrite}
          onNavigateDashboard={onNavigateDashboard}
          onContinueDraft={onContinueDraft}
          onDeleteDraft={onDeleteDraft}
          onReactivateClick={onReactivateClick}
          onPauseClick={onPauseClick}
          onStopClick={onStopClick}
          onEditLiveClick={onEditLiveClick}
          onRemindClick={onRemindClick}
        />
      </Box>
    </Box>
  )
}
